import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarClock,
  Loader2,
  RefreshCw,
  MapPin,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import PageHeader from '../components/ui/PageHeader';
import StatsCard from '../components/ui/StatsCard';
import { Card } from '../components/ui/Card';

interface Appuntamento {
  id: string;
  titolo: string;
  inizio: string; // ISO
  fine: string; // ISO
  luogo: string | null;
  note: string | null;
  allDay: boolean;
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('bymarke_token')}`,
});

const GIORNI = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const MESI = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

// Chiave giorno in ora locale (browser)
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const REFRESH_MS = 5 * 60 * 1000; // aggiorna ogni 5 minuti

const VideomakerAppuntamenti: React.FC = () => {
  const navigate = useNavigate();
  // Stessa pagina per due viste: in programma (default) e già fatti (?passati=1)
  const [searchParams] = useSearchParams();
  const passati = searchParams.get('passati') === '1';
  const [appuntamenti, setAppuntamenti] = useState<Appuntamento[]>([]);
  const [aggiornatoAl, setAggiornatoAl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await fetch(
        `/api/videomaker/appuntamenti${passati ? '?passati=1' : ''}`,
        { headers: authHeaders() }
      );
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Errore di lettura');
      setAppuntamenti(json.data.appuntamenti || []);
      setAggiornatoAl(json.data.aggiornatoAl || null);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Impossibile leggere il calendario');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [passati]);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Raggruppa gli appuntamenti per giorno (già ordinati dal backend)
  const gruppi = useMemo(() => {
    const now = new Date();
    const oggiKey = dayKey(now);
    const domani = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const domaniKey = dayKey(domani);

    const map = new Map<string, { data: Date; etichetta: string; items: Appuntamento[] }>();
    for (const a of appuntamenti) {
      const d = new Date(a.inizio);
      const k = dayKey(d);
      if (!map.has(k)) {
        let etichetta = `${GIORNI[d.getDay()]} ${d.getDate()} ${MESI[d.getMonth()]}`;
        if (k === oggiKey) etichetta = `Oggi · ${etichetta}`;
        else if (k === domaniKey) etichetta = `Domani · ${etichetta}`;
        map.set(k, { data: d, etichetta, items: [] });
      }
      map.get(k)!.items.push(a);
    }
    return Array.from(map.values());
  }, [appuntamenti]);

  // Numero di appuntamenti di oggi (per la StatsCard)
  const appuntamentiOggi = useMemo(() => {
    const oggiKey = dayKey(new Date());
    return appuntamenti.filter((a) => dayKey(new Date(a.inizio)) === oggiKey).length;
  }, [appuntamenti]);

  const orario = (a: Appuntamento) => {
    if (a.allDay) return 'Tutto il giorno';
    const d = new Date(a.inizio);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const aggiornatoTesto = aggiornatoAl
    ? new Date(aggiornatoAl).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate('/videomaker')}
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Torna al Videomaker
      </button>

      {/* Header con stat */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
        <PageHeader
          title={passati ? 'Appuntamenti passati' : 'Appuntamenti'}
          description={
            passati
              ? 'Riprese già fatte negli ultimi 6 mesi · dalla più recente'
              : 'Calendario appuntamenti dei videomaker · aggiornato in tempo reale'
          }
        />
        <StatsCard
          value={passati ? appuntamenti.length : appuntamentiOggi}
          label={passati ? 'appuntamenti passati' : 'appuntamenti oggi'}
          variant="violet"
        />
      </div>

      {/* Barra stato aggiornamento */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          {refreshing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <CalendarClock className="w-3.5 h-3.5" />
          )}
          {aggiornatoTesto ? (
            <span>Aggiornato alle {aggiornatoTesto} · si aggiorna da solo ogni 5 minuti</span>
          ) : (
            <span>Caricamento calendario…</span>
          )}
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Aggiorna
        </button>
      </div>

      {/* Contenuto */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : error ? (
        <Card className="p-6 flex items-center gap-3 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </Card>
      ) : gruppi.length === 0 ? (
        <Card className="p-6 sm:p-8 text-center">
          <CalendarClock className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400" />
          <h3 className="mt-2 text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
            {passati ? 'Nessun appuntamento passato' : 'Nessun appuntamento in programma'}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {passati
              ? 'Qui trovi le riprese già fatte degli ultimi 6 mesi'
              : 'I prossimi appuntamenti appariranno qui'}
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {gruppi.map((g) => (
            <div key={g.etichetta} className="space-y-2.5">
              {/* Intestazione giorno */}
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 capitalize" style={{ letterSpacing: '-0.01em' }}>
                  {g.etichetta}
                </h3>
                <div className="flex-1 h-px bg-slate-200 dark:bg-gray-700" />
                <span className="text-xs text-slate-400">{g.items.length}</span>
              </div>

              {/* Card appuntamenti del giorno */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.items.map((a) => (
                  <Card key={a.id} className="p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-500/15 flex items-center justify-center">
                        <CalendarClock className="w-4.5 h-4.5 text-violet-600 dark:text-violet-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-snug break-words">
                          {a.titolo}
                        </h4>
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                          <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{orario(a)}</span>
                        </div>
                        {a.luogo && (
                          <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{a.luogo}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideomakerAppuntamenti;
